import { subject } from '@casl/ability';
import {
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonPopover,
} from '@ionic/react';
import { ellipsisHorizontalOutline } from 'ionicons/icons';
import Linkify from 'linkify-react';
import React, { ReactElement, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can } from '../../api/authorization.provider';
import { ChatMessage } from '../../api/graphql/generated';
import { useIsMounted } from '../../util/hooks';
import { openLink } from '../../util/link-utils';
import { Avatar } from '../avatar/avatar';
import { DateField } from '../ui/date-field';
import { PopoverState } from '../ui/ui-interfaces';
import { useChatMessageHelper } from './chat-message.hooks';
import './chat.css';
import { ConversationContext } from './conversation-modal-provider';

export interface ConversationMessageProps {
  conversation: ChatMessage;
  containerStyle?: React.CSSProperties;
}

const ConversationMessage = (props: ConversationMessageProps): ReactElement => {
  const { conversation, containerStyle } = props;
  const { i18n } = useTranslation();

  const isMounted = useIsMounted();
  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  } as PopoverState);

  const messageRef = useRef<HTMLDivElement>(null);

  const chatContext = useContext(ConversationContext);

  const classList: string[] = ['chat-message-container'];
  const containerClassList: string[] = [];
  const messageClassList: string[] = ['chat-message'];

  const isThreadMessage = conversation.threadId !== null ? true : false;

  if (isThreadMessage) {
    classList.push('threaded-message');
  }

  const showOptions = (
    e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();
    if (isMounted()) {
      setShowPopover({ showPopover: true, event: e as unknown as Event });
    }
  };

  const hideOptions = () => {
    if (isMounted()) {
      setShowPopover({ showPopover: false, event: undefined });
    }
  };

  const { attachmentList } = useChatMessageHelper({
    allowRemove: false,
    chatId: conversation.chatId,
    conversationId: conversation.id,
    threadId: undefined,
  });

  return (
    <div className={containerClassList.join(' ')}>
      <div className={classList.join(' ')}>
        <Avatar
          attachment={conversation?.author?.avatarAttachment}
          avatarProps={{
            className: 'chat-avatar',
          }}
        />
        <IonLabel className="chat-user-info">
          <h2>{conversation?.author.name}</h2>
          <p>
            <DateField
              date={conversation.editedAt ?? conversation.publishedAt}
            />
            {conversation.editedAt && (
              <span className="chat-edited-badge">
                {i18n.t('common:chat_edited')}
              </span>
            )}
          </p>
        </IonLabel>
        <Can
          I="update"
          this={subject('chat_message', {
            ...conversation,
          })}
        >
          <div className="chat-buttons">
            <IonButton fill="clear" size="small" onClick={showOptions}>
              <IonIcon icon={ellipsisHorizontalOutline} color="medium" />
            </IonButton>
          </div>
        </Can>
        <div className="chat-connector" />
        <div
          ref={messageRef}
          className={messageClassList.join(' ')}
          style={containerStyle}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Linkify
            options={{
              target: '_blank',
              rel: 'noreferrer',
              attributes: (href: string, _type: string) => ({
                onClick: (
                  e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
                ) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openLink(href);
                },
              }),
            }}
          >
            {conversation.message}
          </Linkify>
          {attachmentList}
        </div>
        <IonPopover
          event={popoverState.event}
          isOpen={popoverState.showPopover}
          onDidDismiss={hideOptions}
        >
          <IonItem
            button={true}
            disabled={false}
            lines="none"
            onClick={(e) => {
              e.stopPropagation();
              hideOptions();
              chatContext?.updateMessage(conversation);
            }}
          >
            {i18n.t('common:chat_edit_message')}
          </IonItem>
        </IonPopover>
      </div>
    </div>
  );
};

export { ConversationMessage };
